import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Lock, Loader2, CheckCircle2, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token in the link.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/start"), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset failed. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-6" data-testid="reset-no-token">
        <div className="max-w-md text-center">
          <p className="font-mono-eyebrow text-[10px] uppercase tracking-wider text-[#b94e4e] mb-3">Invalid link</p>
          <h1 className="font-display text-[26px] text-[#0f1724] font-semibold tracking-tight leading-tight mb-3">
            This reset link is missing its token.
          </h1>
          <p className="font-body text-[14px] text-[#4b5563] mb-6">
            It might have been copied incompletely. Request a new one.
          </p>
          <Link to="/forgot-password" className="inline-flex items-center justify-center h-12 px-6 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[14px]">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/start" className="inline-flex items-center gap-2 text-[#486a8f] text-[13px] mb-8 hover:underline" data-testid="reset-back-link">
          <ArrowLeft size={14} /> Back to login
        </Link>
        <div className="bg-white rounded-2xl p-8 shadow-[0_10px_40px_-20px_rgba(15,23,36,0.15)] border border-[#eadfc9]">
          {!done ? (
            <>
              <p className="font-mono-eyebrow text-[10px] uppercase tracking-wider text-[#486a8f] mb-3">Reset password</p>
              <h1 className="font-display text-[26px] text-[#0f1724] font-semibold tracking-tight leading-tight mb-3">
                Choose a new password
              </h1>
              <p className="font-body text-[14px] text-[#4b5563] leading-relaxed mb-6">
                Minimum 8 characters. Once reset, you'll be logged out of all devices.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="font-mono-eyebrow text-[10px] text-[#486a8f] block mb-2">NEW PASSWORD</label>
                  <div className="relative">
                    <Input
                      data-testid="reset-password-input"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="h-12 pl-10 pr-10"
                      required
                      minLength={8}
                      autoFocus
                    />
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#486a8f]" />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#486a8f]"
                      aria-label="Toggle password visibility"
                    >
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="font-mono-eyebrow text-[10px] text-[#486a8f] block mb-2">CONFIRM PASSWORD</label>
                  <div className="relative">
                    <Input
                      data-testid="reset-confirm-input"
                      type={showPw ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      className="h-12 pl-10"
                      required
                    />
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#486a8f]" />
                  </div>
                </div>
                <Button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full h-12 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[14px]"
                >
                  {loading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Updating…</> : "Set new password"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="reset-done-state">
              <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#3e6b52] flex items-center justify-center">
                <CheckCircle2 size={28} className="text-white" />
              </div>
              <p className="font-mono-eyebrow text-[10px] uppercase tracking-wider text-[#3e6b52] mb-3">Done</p>
              <h2 className="font-display text-[22px] text-[#0f1724] font-semibold tracking-tight leading-tight mb-3">
                Password updated
              </h2>
              <p className="font-body text-[14px] text-[#4b5563] leading-relaxed">
                Taking you to login…
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
